import { ReactNode, useId } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Purple/lavender treatment for the Executive Briefing and the Ask RevOptica
 * shell. Scoped through a nested theme so other pages keep the global palette
 * (whose light-mode primary is near-black).
 */
export const advisor = {
  purple: "#5B3FD6",
  purpleDark: "#4A31C0",
  lavender: "#F1EDFF",
  lavenderBorder: "#E2DAFF",
  page: "#F7F6FB",
  card: "#FFFFFF",
  border: "#E7E3F2",
  text: "#1C1832",
  muted: "#5F5B73",
  subtle: "#8A869C",
  up: "#0F8A5F",
  down: "#C2414B",
  risk: { fg: "#B4232F", bg: "#FDECEE" },
  opportunity: { fg: "#13794F", bg: "#E5F6EE" },
  radius: "12px",
  shadow: "0 1px 2px rgba(28,24,50,0.04), 0 2px 10px rgba(91,63,214,0.05)",
  // Categorical stage colours; distinguishable without implying good/bad.
  series: ["#8C7CF0", "#6F9BF5", "#5CC3A0", "#F2C14E", "#F28B9B", "#B9AEF8", "#7FD1E3", "#F5A86B"],
};

const withAdvisorPalette = (outer: Theme) =>
  createTheme(outer, {
    palette: {
      primary: { main: advisor.purple, dark: advisor.purpleDark, contrastText: "#FFFFFF" },
      text: { primary: advisor.text, secondary: advisor.muted },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, borderRadius: 8 } } },
    },
  });

export function AdvisorThemeProvider({ children }: { children: ReactNode }) {
  return <ThemeProvider theme={withAdvisorPalette}>{children}</ThemeProvider>;
}

/** Visually hidden but available to assistive technology and tests. */
export const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

/**
 * Small "i" button that keeps secondary explanations out of the main layout.
 * The text stays in the DOM (described-by) so it is reachable without hover.
 */
export function InfoHint({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  return (
    <>
      <Tooltip title={<Box sx={{ fontSize: 13, lineHeight: 1.45, p: 0.5 }}>{children}</Box>} enterTouchDelay={0} leaveTouchDelay={6000} arrow>
        <IconButton size="small" aria-label={label} aria-describedby={id} sx={{ color: advisor.subtle, p: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Box component="span" id={id} sx={srOnly}>{children}</Box>
    </>
  );
}
